import React, { FC } from 'react';
import { Article } from 't2-graphql-types';
// import { Article, ArticleTypeType } from 't2-graphql-types';
import { PromptHeader } from './PromptHeader';
import PromptDetails from './PromptDetails';
import { TCloudinaryImageShape } from '../CloudinaryResponsiveImage/types';
import { getPromptByIdQuery } from '../CreatePrompt/queries/getPromptByIdQuery';
import { useHasPermission } from '../Permissions/hooks/useHasPermission';
import { getUserDraftArticleForParentArticle } from './queries/getUserDraftArticleForParentArticle';
import { hasPermissionToCreateArticleOnTerritory } from '../Article/services/hasPermissionToCreateArticleOnTerritory';
import { useAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { useCanEditPrompt } from './hooks/useCanEditPrompt';
import { useCanDeletePrompt } from './hooks/useCanDeletePrompt';
import { getArticleEditPageUrl } from '../../services/pagesUrl/gerArticleEditPageUrl';
import { getNewArticlePageUrl } from '../../services/pagesUrl/getNewArticlePageUrl';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';
import { DEFAULT_PROMPT_IMAGE, PromptSettingsLiveStatus } from '../Prompt/constants';
import { useT2Query } from '../../hooks/useT2Query';
import { ReplyToPromptStatus } from './constants';

// TODO: l:3 doesn't work fine
enum ArticleTypeType {
  Article = 'ARTICLE',
  Prompt = 'PROMPT',
  Quote = 'QUOTE',
}

type TPromptHeaderContainerProps = {
  promptId: Article['id'];
};

const PromptHeaderContainer: FC<TPromptHeaderContainerProps> = ({ promptId }) => {
  const { sendEvent } = useAnalytics();
  const { isAuthenticated, loading, authenticatedUserId } = useAuthenticatedUser();
  const hasPermission = useHasPermission();
  const promptResponse = useT2Query(getPromptByIdQuery, { variables: { id: promptId }, returnPartialData: true });
  const { title, author, createdAt, body, articleLink, territory, coverImage } = promptResponse?.data?.article || {};
  const draftArticleForThisPromptResponse = useT2Query(getUserDraftArticleForParentArticle, {
    variables: { parentArticleId: promptId },
  });
  const territoryId = territory?.id;
  const isUserAuthor = !!authenticatedUserId && authenticatedUserId === author?.id;
  const canEditPrompt = useCanEditPrompt(territoryId) && isUserAuthor;
  const canDeletePrompt = useCanDeletePrompt(territoryId) && isUserAuthor;
  const draftArticleForThisPrompt = draftArticleForThisPromptResponse?.data?.authenticatedItem?.articles?.[0]?.id;
  const promptSettingsLiveStatus = promptResponse?.data?.article?.promptSettings?.liveStatus;
  const replyToPromptStatus = ((): ReplyToPromptStatus => {
    if (loading) {
      return ReplyToPromptStatus.HIDDEN;
    }
    if (!isAuthenticated) {
      return ReplyToPromptStatus.NEW;
    }
    if (!hasPermissionToCreateArticleOnTerritory(hasPermission, ArticleTypeType.Prompt, territory?.id)) {
      return ReplyToPromptStatus.FORBIDDEN;
    }
    if (draftArticleForThisPrompt) {
      return ReplyToPromptStatus.CONTINUE;
    }
    return ReplyToPromptStatus.NEW;
  })();

  const replyToPromptUrl =
    replyToPromptStatus === ReplyToPromptStatus.NEW
      ? getNewArticlePageUrl({
          parentArticleId: promptId,
        })
      : getArticleEditPageUrl(draftArticleForThisPrompt);

  const onReplyToPromptClick = () => {
    sendEvent('prompt_reply_button_click', { prompt_id: promptId });
  };

  const coverImageUrl = coverImage?.publicUrlTransformed || DEFAULT_PROMPT_IMAGE;

  return (
    <>
      <PromptHeader
        title={title as string}
        body={body}
        replyToPromptStatus={replyToPromptStatus}
        replyToPromptUrl={replyToPromptUrl}
        promptId={promptId}
        onReplyToPromptClick={onReplyToPromptClick}
        promptSettingsLiveStatus={promptSettingsLiveStatus as PromptSettingsLiveStatus}
        isPromptLoading={promptResponse?.loading}
        isAuthenticatedUserLoading={loading}
      />
      <PromptDetails
        authorId={author?.id as string}
        name={author?.name as string}
        username={author?.username as string}
        profileImage={(author?.profileImage as TCloudinaryImageShape) || ''}
        createdAt={createdAt}
        url={articleLink}
        canEditPrompt={canEditPrompt}
        canDeletePrompt={canDeletePrompt}
        territorySlug={territory?.slug}
        promptData={promptResponse?.data?.article || {}}
        territoryId={territoryId}
        coverImage={coverImageUrl}
      />
    </>
  );
};

export default PromptHeaderContainer;
